import { useEffect, useState } from "react";
import {
  ActivitySample,
  AdminSensor,
  SensorPatch,
  listSensorActivity,
  listSensors,
  sensorKindLabel,
  sensorVersionLabel,
  updateSensor,
} from "../../../Services/Admin/Sensors";
import {
  FirmwareRelease,
  listFirmware,
} from "../../../Services/Admin/Firmware";
import DeleteSensorButton from "./DeleteSensorButton";
import VenuePicker from "./VenuePicker";
import { useVenueOptions } from "./useVenueOptions";
import "./SensorsPanel.css";

/**
 * Sensorpanelen: vad varje låda mäter, vad den senast mätte, och vilken
 * firmware den kör.
 *
 * Rådatan visas med flit. Bandgränserna i activity_math.h är
 * provisoriska och ska justeras mot verkliga kvällar — det går inte utan
 * att kunna se vad Wi-Fi, BLE och ljudet faktiskt visade när någon
 * antecknade "det var fullt".
 */

function timeAgo(iso: string | null) {
  if (!iso) return "never";
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

const HIDDEN_REASONS: Record<string, string> = {
  stale: "too old to show publicly",
  no_baseline: "no baseline — calibrate while closed",
  no_index: "no index could be computed",
  low_confidence: "confidence too low",
};

function SampleDetails({ sample }: { sample: ActivitySample }) {
  return (
    <div className="sensor-sample">
      <div className="sensor-sample-head">
        <span className={`sensor-band sensor-band--${sample.crowdLevel.toLowerCase()}`}>
          {sample.crowdLevel}
        </span>
        <span>
          index {sample.activityIndex ?? "—"} · confidence{" "}
          {sample.confidence.toFixed(2)}
        </span>
        <span className="sensor-dim">{timeAgo(sample.measuredAt)}</span>
      </div>

      {!sample.publicVisible && (
        <p className="sensor-warn">
          Hidden from the map:{" "}
          {HIDDEN_REASONS[sample.hiddenReason ?? ""] ?? sample.hiddenReason}
        </p>
      )}

      <div className="sensor-sources">
        <div>
          <strong>Wi-Fi</strong>
          {sample.wifi ? (
            <span>
              {sample.wifi.managementPacketsPerSecond?.toFixed(1)} mgmt/s ·{" "}
              {sample.wifi.dataPacketsPerSecond?.toFixed(1)} data/s ·{" "}
              {sample.wifi.meanRssi?.toFixed(0) ?? "—"} dBm ·{" "}
              {sample.wifi.sampleSeconds?.toFixed(0)} s
            </span>
          ) : (
            <span className="sensor-dim">no reading</span>
          )}
        </div>

        <div>
          <strong>BLE</strong>
          {sample.ble ? (
            <span>
              {sample.ble.advertisementsPerSecond?.toFixed(1)} adv/s ·{" "}
              {sample.ble.meanRssi?.toFixed(0) ?? "—"} dBm ·{" "}
              {sample.ble.sampleSeconds?.toFixed(0)} s
            </span>
          ) : (
            <span className="sensor-dim">no reading</span>
          )}
        </div>

        <div>
          <strong>Audio</strong>
          {sample.audio ? (
            <span>
              {sample.audio.rmsDbfs?.toFixed(1)} dBFS rms ·{" "}
              {sample.audio.peakDbfs?.toFixed(1)} peak ·{" "}
              {((sample.audio.clippingRatio ?? 0) * 100).toFixed(2)} % clipped
            </span>
          ) : (
            <span className="sensor-dim">no reading</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SensorsPanel() {
  const [sensors, setSensors] = useState<AdminSensor[]>([]);
  const [releases, setReleases] = useState<FirmwareRelease[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [history, setHistory] = useState<ActivitySample[]>([]);
  const [sensorQuery, setSensorQuery] = useState("");
  const [sensorFilter, setSensorFilter] = useState<"all" | "online" | "offline" | "inactive" | "ota_failed" | "software" | "hardware">("all");

  const venueOptions = useVenueOptions();

  const load = () => {
    setLoading(true);
    setError(null);

    Promise.all([listSensors(), listFirmware()])
      .then(([sensorData, firmwareData]) => {
        setSensors(sensorData.items.filter((sensor) => sensor.claimStatus === "CLAIMED"));
        // Bara det som faktiskt går att peka en sensor mot. Draft och
        // archived vägrar servern ändå.
        setReleases(
          firmwareData.items.filter(
            (r) => r.status === "canary" || r.status === "stable"
          )
        );
      })
      .catch((e) => setError(e?.message ?? "Could not load sensors"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const visibleSensors = sensors.filter((sensor) => {
    const query = sensorQuery.trim().toLowerCase();
    const matchesQuery = !query || [
      sensor.installationName,
      sensor.sensorId,
      sensor.venue?.name,
      sensor.ota.firmwareVersion,
      sensor.agentVersion,
      sensorKindLabel(sensor),
    ].some((value) => value?.toLowerCase().includes(query));
    const matchesStatus =
      sensorFilter === "all" ||
      (sensorFilter === "online" && sensor.online) ||
      (sensorFilter === "offline" && !sensor.online) ||
      (sensorFilter === "inactive" && !sensor.isActive) ||
      (sensorFilter === "ota_failed" && sensor.ota.status === "failed") ||
      (sensorFilter === "software" && sensor.deviceType === "SOFTWARE") ||
      (sensorFilter === "hardware" && sensor.deviceType !== "SOFTWARE");
    return matchesQuery && matchesStatus;
  });

  const patch = async (sensor: AdminSensor, body: SensorPatch) => {
    setBusyId(sensor.sensorId);
    setError(null);

    try {
      const res = await updateSensor(sensor.sensorId, body);
      setSensors((prev) =>
        prev.map((s) =>
          s.sensorId === sensor.sensorId
            ? // Svaret innehåller inte mätningen, så den behålls från
              // listan i stället för att blinka bort vid varje ändring.
              { ...s, ...res.sensor, activity: { ...res.sensor.activity, latestSample: s.activity.latestSample } }
            : s
        )
      );
    } catch (e: any) {
      setError(e?.message ?? "The update failed");
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Byte av venue laddar om listan i stället för att skriva om raden.
   * PATCH-svaret bär bara venueId, inte venuets namn, och en rad som
   * säger "no venue" direkt efter en lyckad koppling ser ut som ett fel.
   */
  const setVenue = async (sensor: AdminSensor, venueId: string) => {
    if (!venueId || venueId === sensor.venue?.id) return;

    setBusyId(sensor.sensorId);
    setError(null);
    setNotice(null);

    try {
      await updateSensor(sensor.sensorId, { venueId });
      const venue = venueOptions.options.find((option) => option.id === venueId);
      setNotice(
        `${sensor.installationName ?? sensor.sensorId} is now assigned to ${venue?.name ?? "the selected venue"}.`
      );
      load();
    } catch (e: any) {
      setError(e?.message ?? "Could not assign the sensor");
    } finally {
      setBusyId(null);
    }
  };

  /** Lossar sensorn från sitt venue. Raden flyttar till oanslutna. */
  const disconnect = async (sensor: AdminSensor) => {
    setBusyId(sensor.sensorId);
    setError(null);
    setNotice(null);

    try {
      await updateSensor(sensor.sensorId, { venueId: null });
      setSensors((prev) => prev.filter((s) => s.sensorId !== sensor.sensorId));
      setNotice(
        `${sensor.installationName ?? sensor.sensorId} is disconnected and now appears under Unclaimed sensors.`
      );
    } catch (e: any) {
      setError(e?.message ?? "Could not disconnect the sensor");
    } finally {
      setBusyId(null);
    }
  };

  const onDeleted = ({
    sensorId,
    credentialRevoked,
  }: {
    sensorId: string;
    credentialRevoked: boolean;
  }) => {
    setSensors((prev) => prev.filter((s) => s.sensorId !== sensorId));
    setNotice(
      credentialRevoked
        ? `${sensorId} was deleted and its credential was revoked.`
        : `${sensorId} was deleted. It will return as unclaimed if it reports in again.`
    );
  };

  const toggleHistory = async (sensor: AdminSensor) => {
    if (historyFor === sensor.sensorId) {
      setHistoryFor(null);
      setHistory([]);
      return;
    }

    try {
      const data = await listSensorActivity(sensor.sensorId, 50);
      setHistoryFor(sensor.sensorId);
      setHistory(data.items);
    } catch (e: any) {
      setError(e?.message ?? "Could not load history");
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <button onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <input
          className="admin-toolbar-grow"
          value={sensorQuery}
          onChange={(event) => setSensorQuery(event.target.value)}
          placeholder="Search sensor, venue or firmware…"
          aria-label="Search sensors"
        />
        <select value={sensorFilter} onChange={(event) => setSensorFilter(event.target.value as typeof sensorFilter)}>
          <option value="all">All sensors</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="inactive">Inactive</option>
          <option value="ota_failed">OTA failed</option>
          <option value="software">Software (Beatspot Agent)</option>
          <option value="hardware">Hardware</option>
        </select>
      </div>

      {(error || venueOptions.error) && (
        <div className="admin-error">{error ?? venueOptions.error}</div>
      )}
      {notice && <div className="admin-notice">{notice}</div>}
      {!loading && visibleSensors.length === 0 && (
        <p className="admin-empty">No connected sensors match these filters.</p>
      )}

      <div className="sensor-fleet-list">
      {visibleSensors.map((sensor) => {
        const busy = busyId === sensor.sensorId;
        const activity = sensor.activity;
        const software = sensor.deviceType === "SOFTWARE";

        return (
          <details className="sensor-card sensor-fleet-card" key={sensor.sensorId}>
            <summary className="sensor-head sensor-fleet-summary">
              <div className="sensor-head-main">
                <strong>{sensor.installationName ?? sensor.sensorId}</strong>
                <span className="sensor-dim sensor-id">{sensor.sensorId}</span>
              </div>
              <div className="sensor-fleet-meta">
                <span>{sensor.venue?.name ?? "No venue"}</span>
                <span className={sensor.online ? "admin-status admin-status--online" : "admin-status admin-status--offline"}>{sensor.online ? "Online" : "Offline"}</span>
                <span className={sensor.isActive ? "admin-status admin-status--active" : "admin-status admin-status--inactive"}>{sensor.isActive ? "Active" : "Inactive"}</span>
                {/* Beatspot Agent skickar status "paused" när venuet pausat delningen i appen. */}
                {software && sensor.lastStatus === "paused" && (
                  <span className="admin-status admin-status--inactive">Paused on computer</span>
                )}
                <span>Seen {timeAgo(sensor.lastSeenAt)}</span>
                <span>{sensorKindLabel(sensor)}</span>
                <span>{sensorVersionLabel(sensor)}</span>
                {!software && (
                  <>
                    <span className={`admin-status admin-status--${sensor.ota.status ?? "none"}`}>OTA {sensor.ota.status ?? "—"}</span>
                    <span>{activity.latestSample?.crowdLevel ?? "No activity"}</span>
                  </>
                )}
              </div>
            </summary>

            <div className="sensor-fleet-details">

            <fieldset className="sensor-section" disabled={busy}>
              <legend>Identity</legend>
              <label>
                Display name
                <input
                  type="text"
                  maxLength={48}
                  defaultValue={sensor.installationName ?? ""}
                  onBlur={(event) => {
                    const value = event.target.value.trim();
                    if (value && value !== sensor.installationName) {
                      patch(sensor, { installationName: value });
                    }
                  }}
                />
              </label>
              <p className="sensor-dim">
                Technical identity: {sensor.sensorId}. Renaming does not change identity or authentication.
              </p>

              <label className="sensor-toggle">
                <input
                  type="checkbox"
                  checked={sensor.isActive}
                  onChange={(e) => patch(sensor, { isActive: e.target.checked })}
                />
                Enabled
              </label>
              <p className="sensor-dim">
                A disabled sensor keeps sending heartbeats, so it stays visible
                here, but records no plays.
              </p>

              <div className="sensor-venue-row">
                <VenuePicker
                  current={sensor.venue}
                  value={sensor.venue?.id ?? ""}
                  options={venueOptions.options}
                  disabled={busy}
                  label="Venue"
                  onChange={(venueId) => setVenue(sensor, venueId)}
                />
                <button
                  type="button"
                  className="admin-btn--danger"
                  disabled={busy || !sensor.venue}
                  onClick={() => disconnect(sensor)}
                >
                  Disconnect from venue
                </button>
              </div>
              <p className="sensor-dim">
                A disconnected sensor stops measuring for this venue and moves
                to Unclaimed sensors. Existing telemetry is retained.
              </p>
            </fieldset>

            {/* Beatspot Agent läser datorns Now Playing. Mikrofon, aktivitet
                och firmware finns bara i lådan, och servern nekar dem. */}
            {software ? (
              <fieldset className="sensor-section">
                <legend>Beatspot Agent</legend>
                <p className="sensor-dim">
                  Reads the computer's Now Playing ({sensorKindLabel(sensor)})
                  {sensor.venue
                    ? ` and reports once every ${sensor.intervalSeconds} s. Set under Venues → ${sensor.venue.name} → Edit.`
                    : ". Reports nothing until it is connected to a venue."}
                </p>
                <p className="sensor-dim">
                  Last status: {sensor.lastStatus ?? "—"} ({timeAgo(sensor.lastSeenAt)})
                </p>
                <p className="sensor-dim">
                  Clip length, microphone tuning, activity measurement and
                  remote firmware updates: N/A for software sensors. The agent
                  is updated through Homebrew or WinGet.
                </p>
              </fieldset>
            ) : (
              <>
            {/* ---------------- Aktivitetsmätning ---------------- */}
            <fieldset className="sensor-section" disabled={busy}>
              <legend>Activity (experimental)</legend>

              {/* Intervall och vad som mäts ställs på venuet, inte här. */}
              {sensor.venue ? (
                <p className="sensor-dim">
                  Recognition every {sensor.intervalSeconds} s ({sensor.recordSeconds} s clips).
                  Activity{" "}
                  {activity.enabled
                    ? `every ${activity.intervalSeconds} s · ${[
                        activity.wifiEnabled && "Wi-Fi",
                        activity.bleEnabled && "BLE",
                        activity.audioEnabled && "audio",
                      ]
                        .filter(Boolean)
                        .join(", ") || "no sources"}`
                    : "off"}
                  . Set under Venues → {sensor.venue.name} → Edit.
                </p>
              ) : (
                <p className="sensor-dim">
                  Not measuring. Settings come from the venue once the sensor is connected.
                </p>
              )}

              {/* Kalibreringen är hela grunden för indexet: utan den vet
                  sensorn inte vad "tyst" är för just den här lokalen. */}
              <div className="sensor-calibration">
                <label className="sensor-toggle">
                  <input
                    type="checkbox"
                    checked={activity.baselineRequested}
                    onChange={(e) =>
                      patch(sensor, {
                        activityBaselineRequested: e.target.checked,
                      })
                    }
                  />
                  Calibrate baseline at the next window
                </label>
                <p className="sensor-dim">
                  Only turn this on while the venue is actually closed and quiet.
                  The flag clears itself once the sensor acknowledges it.
                  {activity.baselineCalibratedAt
                    ? ` Last calibrated ${timeAgo(activity.baselineCalibratedAt)}.`
                    : " Not calibrated yet."}
                </p>
              </div>

              {activity.latestSample ? (
                <SampleDetails sample={activity.latestSample} />
              ) : (
                <p className="sensor-dim">No reading received yet.</p>
              )}

              <button type="button" onClick={() => toggleHistory(sensor)}>
                {historyFor === sensor.sensorId
                  ? "Hide history"
                  : "Show history"}
              </button>

              {historyFor === sensor.sensorId && (
                <div className="sensor-history">
                  {history.length === 0 && (
                    <p className="sensor-dim">No history.</p>
                  )}
                  {history.map((sample) => (
                    <div className="sensor-history-row" key={sample.id}>
                      <span className="sensor-dim">
                        {new Date(sample.measuredAt).toLocaleString()}
                      </span>
                      <span>{sample.crowdLevel}</span>
                      <span>idx {sample.activityIndex ?? "—"}</span>
                      <span>conf {sample.confidence.toFixed(2)}</span>
                      <span className="sensor-dim">
                        {sample.sources.join(", ") || "no sources"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </fieldset>

            {/* ---------------- Fjärruppdatering ---------------- */}
            <fieldset className="sensor-section" disabled={busy}>
              <legend>Remote update</legend>

              <label className="sensor-toggle">
                <input
                  type="checkbox"
                  checked={sensor.ota.enabled}
                  onChange={(e) =>
                    patch(sensor, { otaEnabled: e.target.checked })
                  }
                />
                Allow the sensor to update itself
              </label>

              <label>
                Version
                <select
                  value={sensor.ota.target?.id ?? ""}
                  onChange={(e) =>
                    patch(sensor, { otaTargetId: e.target.value || null })
                  }
                >
                  <option value="">Follow stable</option>
                  {releases.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.version} ({r.status})
                    </option>
                  ))}
                </select>
              </label>

              {sensor.ota.status && (
                <p
                  className={
                    sensor.ota.status === "failed"
                      ? "sensor-warn"
                      : "sensor-dim"
                  }
                >
                  {sensor.ota.status}
                  {sensor.ota.message ? ` — ${sensor.ota.message}` : ""}
                  {sensor.ota.updatedAt
                    ? ` (${timeAgo(sensor.ota.updatedAt)})`
                    : ""}
                </p>
              )}
            </fieldset>
              </>
            )}

            <div className="sensor-danger">
              <DeleteSensorButton
                sensorId={sensor.sensorId}
                name={sensor.installationName}
                disabled={busy}
                onDeleted={onDeleted}
                onError={setError}
              />
            </div>
            </div>
          </details>
        );
      })}
      </div>
    </div>
  );
}
